import './index.css'

// Register Sentry as early as possible, which will also capture errors from the PWA registration
import './instrument'

// It's important to import the PWA registration as early as possible
import './pwa'

import { lazy, Suspense } from 'react'
import ReactDOM from 'react-dom/client'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router'

import { UserProvider } from './auth/userContext'
import ProtectedRoute from './components/common/ProtectedRoute'
import { ErrorBoundary } from './components/ErrorBoundary'
import { PageLoadingSpinner } from './components/ui/PageLoadingSpinner'
import { ActivityProvider } from './hooks/useActivity'
import { OnlineStatusProvider } from './hooks/useOnlineStatus'
import { offlinePhotoStorage } from './utils/offlineStorage'
import { photoSyncService } from './utils/photoSyncService'

// Lazy-loaded route components
const Activity = lazy(() => import('./routes/activity'))
const Admin = lazy(() => import('./routes/admin'))
const Debug = lazy(() => import('./routes/debug'))
const DefaultRoute = lazy(() => import('./routes/default-route'))
const DeprecatedHome = lazy(() => import('./routes/deprecated-home'))
const DueDiligenceLayout = lazy(() => import('./routes/due-diligence/_layout'))
const DueDiligenceForm = lazy(() => import('./routes/due-diligence/form'))
const DueDiligenceNotes = lazy(() => import('./routes/due-diligence/notes'))
const EMCPIndex = lazy(() => import('./routes/eMCP/_index'))
const CompanyAdmin = lazy(() => import('./routes/eMCP/admin/index'))
const QRCodeAuth = lazy(() => import('./routes/eMCP/qrcode'))
const ReportViewer = lazy(() => import('./routes/eMCP/reports/$reportId'))
const ReportsIndex = lazy(() => import('./routes/eMCP/reports/index'))
const ScanQRCode = lazy(() => import('./routes/eMCP/scan'))
const SurveyQualityDashboard = lazy(
  () => import('./routes/eMCP/survey-quality/index'),
)
const SurveyIndex = lazy(() => import('./routes/eMCP/survey/index'))
const SurveyForm = lazy(() => import('./routes/eMCP/survey/SurveyForm'))
const SurveyLayout = lazy(() => import('./routes/eMCP/survey/SurveyLayout'))
const TestDetail = lazy(() => import('./routes/eMCP/tests/detail'))
const LogCallback = lazy(() => import('./routes/eMCP/units/callbacks'))
const CallbackDetail = lazy(
  () => import('./routes/eMCP/units/callbacks/detail'),
)
const UnitDetail = lazy(() => import('./routes/eMCP/units/detail'))
const MaintenanceDetail = lazy(
  () => import('./routes/eMCP/units/maintenance/detail'),
)
const LogMaintenance = lazy(
  () => import('./routes/eMCP/units/maintenance/index'),
)
const McpYearlyView = lazy(() => import('./routes/eMCP/units/mcp-yearly'))
const LogOil = lazy(() => import('./routes/eMCP/units/oil'))
const OilRecordDetail = lazy(() => import('./routes/eMCP/units/oil/detail'))
const LogRepair = lazy(() => import('./routes/eMCP/units/repairs'))
const RepairDetail = lazy(() => import('./routes/eMCP/units/repairs/detail'))
const LogTest = lazy(() => import('./routes/eMCP/units/tests'))
const UnitDetailsPage = lazy(() => import('./routes/eMCP/units/unit-details'))
const UnitLayout = lazy(() => import('./routes/eMCP/units/UnitLayout'))
const AcceptInvitation = lazy(() => import('./routes/invitations/accept'))
const AcceptCompanyUserInvitation = lazy(
  () => import('./routes/invitations/company-user-accept'),
)
const RootLayout = lazy(() => import('./routes/layout'))
const ForgotPassword = lazy(() => import('./routes/forgot-password'))
const AuthAction = lazy(() => import('./routes/auth/action'))
const Login = lazy(() => import('./routes/login'))
const Logout = lazy(() => import('./routes/logout'))
const Profile = lazy(() => import('./routes/profile'))
const Reports = lazy(() => import('./routes/reports'))
const ClientReportViewer = lazy(() => import('./routes/reports.$reportId'))
const CompanyDetail = lazy(() => import('./components/CompanyDetail'))

// Redirect component that preserves search params
function SurveyIndexRedirect() {
  const searchParams = new URLSearchParams(window.location.search)
  return <Navigate to={`elevator-type?${searchParams.toString()}`} replace />
}

// Initialize offline photo storage and sync service
offlinePhotoStorage.init().catch((error) => {
  console.error('Failed to initialize offline photo storage:', error)
})

photoSyncService.init()

// Clean up sync service on page unload
window.addEventListener('beforeunload', () => {
  photoSyncService.destroy()
})

const root = document.getElementById('root')

if (!root) {
  throw new Error('Root element not found')
}

ReactDOM.createRoot(root).render(
  <BrowserRouter>
    <OnlineStatusProvider>
      <UserProvider>
        <ActivityProvider>
          <Suspense fallback={<PageLoadingSpinner />}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/auth/action" element={<AuthAction />} />
              <Route path="/logout" element={<Logout />} />
              <Route
                path="/invitations/accept"
                element={<AcceptInvitation />}
              />
              <Route
                path="/invitations/company-user/accept"
                element={<AcceptCompanyUserInvitation />}
              />
              <Route path="/emcp/qrcode/:unitId" element={<QRCodeAuth />} />
              <Route element={<ProtectedRoute />}>
                <Route element={<RootLayout />}>
                  <Route index element={<DefaultRoute />} />
                  <Route path="deprecated-home" element={<DeprecatedHome />} />
                  <Route path="reports" element={<Reports />} />
                  <Route
                    path="reports/:reportId"
                    element={<ClientReportViewer />}
                  />
                  <Route path="profile" element={<Profile />} />
                  <Route path="debug" element={<Debug />} />
                  <Route path="admin" element={<Admin />} />
                  <Route
                    path="admin/company/:companyId"
                    element={<CompanyDetail />}
                  />
                  <Route path="due-diligence" element={<DueDiligenceLayout />}>
                    <Route index element={<DueDiligenceNotes />} />
                    <Route path="form" element={<DueDiligenceForm />} />
                  </Route>
                  <Route
                    path="emcp/*"
                    element={
                      <ErrorBoundary>
                        <Routes>
                          <Route index element={<EMCPIndex />} />
                          <Route path="scan" element={<ScanQRCode />} />
                          <Route path="reports" element={<ReportsIndex />} />
                          <Route
                            path="reports/:reportId"
                            element={<ReportViewer />}
                          />
                          <Route path="admin" element={<CompanyAdmin />} />
                          <Route
                            path="survey-quality"
                            element={<SurveyQualityDashboard />}
                          />
                          <Route path="survey" element={<SurveyIndex />} />
                          <Route
                            path="survey/:unitId"
                            element={<SurveyLayout />}
                          >
                            <Route index element={<SurveyIndexRedirect />} />
                            <Route
                              path=":currentStep"
                              element={<SurveyForm />}
                            />
                          </Route>
                          <Route path="activity" element={<Activity />} />
                          <Route
                            path=":siteId/units/:unitId"
                            element={<UnitLayout />}
                          >
                            <Route path="" element={<UnitDetail />} />
                            <Route
                              path="details"
                              element={<UnitDetailsPage />}
                            />
                            <Route
                              path="maintenance"
                              element={<LogMaintenance />}
                            />
                            <Route
                              path="maintenance/:programId/:year/:month"
                              element={<MaintenanceDetail />}
                            />
                            <Route path="callbacks" element={<LogCallback />} />
                            <Route
                              path="callbacks/:callbackId"
                              element={<CallbackDetail />}
                            />
                            <Route path="tests" element={<LogTest />} />
                            <Route
                              path="tests/:testId"
                              element={<TestDetail />}
                            />
                            <Route path="repairs" element={<LogRepair />} />
                            <Route
                              path="repairs/:repairId"
                              element={<RepairDetail />}
                            />
                            <Route path="oil" element={<LogOil />} />
                            <Route
                              path="oil/:oilRecordId"
                              element={<OilRecordDetail />}
                            />
                            <Route
                              path="mcp-yearly"
                              element={<McpYearlyView />}
                            />
                          </Route>
                        </Routes>
                      </ErrorBoundary>
                    }
                  />
                </Route>
              </Route>
            </Routes>
          </Suspense>
        </ActivityProvider>
      </UserProvider>
    </OnlineStatusProvider>
  </BrowserRouter>,
)
